import { AMBIENCE, REACH, voiceFor } from './voices.js';
import { bearing, mixFor } from './falloff.js';

/**
 * The building, heard.
 *
 * Four things that do not know about each other meet here and nowhere else:
 * `voices.js` says what a sound is made of, `falloff.js` says how loud it is
 * from where you are standing, `synth.js` makes the noise and `engine.js` owns
 * the fader. This is the only file that holds all four, and it is deliberately
 * the only one — swapping the samples in is `voices.js`, changing how far a
 * noise carries is `falloff.js`, and neither touches the other.
 *
 * ---------------------------------------------------------------------------
 * IT READS. IT DOES NOT WRITE.
 *
 * The same contract the rig got, for the same reason. This layer imports
 * nothing from `src/sim` or `src/data`. It is handed plain numbers — a noise
 * event flattened to a cause, a position and a radius, and once a frame a
 * reading of where everybody is — and it holds no reference to the player, a
 * cart, the perception system or anything else that could be changed by
 * touching it. There is no path from a sound back into the game.
 *
 * Which matters more here than it looks. The simulation's noise radii are not
 * decoration: they are exactly how far away a witness can hear you, and a
 * write-up is what happens when one does. A layer that could nudge a radius
 * while making it audible would be changing the rules in the act of explaining
 * them.
 * ---------------------------------------------------------------------------
 *
 * ---------------------------------------------------------------------------
 * AUDIO-ONLY SOUNDS NEVER GO ON THE BUS
 *
 * Footsteps, doors, the time clock and the BEO sheet are not noise events in
 * the simulation and they are not being made into some. Putting them on the bus
 * would mean every witness in the building could suddenly hear the player walk,
 * which is a gameplay change of the largest possible kind arriving inside a
 * commit about sound. They are cued straight into this file instead, heard by
 * the player and by nobody else.
 * ---------------------------------------------------------------------------
 */

/** Metres a body covers between footfalls, when nobody says otherwise. */
const STRIDE = 0.65;

/**
 * How often a still-running sound asks whether there is a wall in the way.
 *
 * Every frame would be sixty segment tests per moving source, and a cart
 * crossing a doorway does not change its mind sixty times a second. Every sixth
 * is under a tenth of a second, which is faster than the crossfade that hides
 * the change anyway.
 */
const OCCLUSION_EVERY = 6;

/** The most one-shots allowed to start in a single frame. */
const VOICE_BUDGET = 6;

/** How many decisions the probe remembers. Enough to check a run, not a log. */
const PROBE_DEPTH = 400;

export function createSoundboard({
  /*
   * Where the sound comes out. Optional, and the whole layer works without one:
   * every decision is still made and still recorded, there is simply nowhere
   * for it to go. That is what lets a headless browser with no audio device
   * check the whole of this.
   */
  engine = null,
  /**
   * Whether the building is between these two points.
   *
   * Supplied rather than computed, because working it out needs the level, and
   * the level is not something this file is allowed to hold. It is handed the
   * same segment test the roster's eyes use, so a wall that blocks a manager's
   * view of you muffles the same sound by the same wall.
   */
  blocked = () => false,
  stride = STRIDE,
} = {}) {
  /** Continuous voices, by key: the cart, the room. */
  const running = new Map();
  /** Where everybody was last frame, and how far through a step they are. */
  const walkers = new Map();
  /** The last few decisions, for anything that wants to check rather than hear. */
  let probe = [];
  /**
   * How many decisions have ever been made.
   *
   * On every entry, because the probe is a ring and a caller that remembered a
   * position in it was remembering a place that moves. A check that noted "the
   * probe was twenty long, so anything after the twentieth is mine" read
   * nothing at all the moment the ring was full — which it is within a few
   * seconds of walking anywhere. A number that only ever goes up cannot do
   * that.
   */
  let seq = 0;
  /** How many times each sound has been made. Never reset by anything but a wipe. */
  const counts = new Map();

  let listener = { x: 0, y: 0, z: 0, yaw: 0 };
  let acuity = 1;
  let zone = null;
  let frames = 0;
  let spent = 0;
  let enabled = true;
  /**
   * How far a ridden cart is currently carrying, as the simulation last said.
   *
   * The one place a noise event changes a *continuous* sound rather than making
   * a discrete one. `wheeled.js` emits a ride noise every few feet with a radius
   * that grows with speed and with how loaded the cart is, and that radius is
   * the single most useful number in the game to be able to hear: it is exactly
   * how far down the corridor somebody will hear you coming. So it is not played
   * as a thud — it is fed straight into the reach of the roll that is already
   * running, and the cart gets audibly bigger as it gets faster.
   */
  let ridden = null;

  /** Where a sound is, from the ear. */
  function placeOf(at) {
    return bearing({ from: listener, to: at, yaw: listener.yaw });
  }

  /**
   * The whole decision for one sound, and the record of it.
   *
   * Everything audible in the game goes through this, including the things the
   * simulation never hears about, so that there is exactly one place where
   * distance, acuity and walls are applied and exactly one place to look when
   * something is louder than it ought to be.
   */
  function decide({ name, at, radius, cause = null, trim = 1 }) {
    const place = placeOf(at);
    const wall = blocked(listener, at);
    const mix = mixFor({ distance: place.distance, radius, acuity, blocked: wall });
    seq += 1;
    const decision = {
      seq,
      name,
      cause,
      at,
      place,
      radius,
      reach: mix.reach,
      distance: place.distance,
      gain: mix.gain * trim,
      open: mix.open,
      hz: mix.hz,
      blocked: mix.blocked,
      through: mix.through,
      audible: mix.audible && mix.gain * trim > 0,
    };
    probe = [...probe, decision].slice(-PROBE_DEPTH);
    return decision;
  }

  /** Plays one, if it is audible and there is room in the frame for it. */
  function fire(decision, { rate = 1 } = {}) {
    counts.set(decision.name, (counts.get(decision.name) ?? 0) + 1);
    if (!decision.audible || !enabled) return null;
    if (spent >= VOICE_BUDGET) return null;
    const recipe = voiceFor(decision.name);
    if (!recipe) return null;
    spent += 1;
    return (
      engine?.synth?.play(recipe, {
        gain: decision.gain,
        hz: decision.hz,
        at: decision.place,
        rate,
      }) ?? null
    );
  }

  /**
   * Starts, updates or stops a sound that runs until it is told to stop.
   *
   * One function for all three because a cart that is still rolling, a cart that
   * has just started and a cart that has stopped are the same question asked
   * three frames apart, and writing them as three cases is how a layer ends up
   * with two carts running.
   */
  function sustain(key, name, { at, radius, on, trim = 1, rate = 1, recheck = false }) {
    const held = running.get(key);
    if (!on) {
      if (held) {
        held.voice?.stop();
        running.delete(key);
      }
      return null;
    }

    if (!held) {
      const decision = decide({ name, at, radius, trim });
      counts.set(name, (counts.get(name) ?? 0) + 1);
      const voice = enabled && decision.audible
        ? engine?.synth?.play(voiceFor(name), {
            gain: decision.gain,
            hz: decision.hz,
            at: decision.place,
            rate,
          }) ?? null
        : null;
      const entry = { name, voice, decision, since: frames };
      running.set(key, entry);
      return entry;
    }

    // Moving it costs nothing; asking about the wall does, so that is paced.
    const place = placeOf(at);
    const wall = recheck ? blocked(listener, at) : held.decision.blocked;
    const mix = mixFor({ distance: place.distance, radius, acuity, blocked: wall });
    held.decision = {
      ...held.decision,
      at,
      place,
      radius,
      reach: mix.reach,
      distance: place.distance,
      gain: mix.gain * trim,
      open: mix.open,
      hz: mix.hz,
      blocked: mix.blocked,
      through: mix.through,
      audible: mix.audible,
    };
    /*
     * Written into the record on the same beat the wall is rechecked.
     *
     * Not every frame: a cart crossing the building would fill the ring in two
     * seconds and a check looking for a footstep would find nothing but cart.
     * Not never either — that was the first version, and it meant the one sound
     * in the game that runs for a hundred and forty feet was the one sound
     * nothing could look at while it was running.
     */
    if (recheck) {
      seq += 1;
      probe = [...probe, { ...held.decision, seq }].slice(-PROBE_DEPTH);
    }
    held.voice?.move(place);
    held.voice?.set({ gain: mix.gain * trim, hz: mix.hz, rate });
    /*
     * A source that walked out of earshot while playing is stopped rather than
     * left at nothing, or the building slowly fills with silent carts.
     */
    if (!mix.audible) {
      held.voice?.stop(0.3);
      running.delete(key);
    }
    return held;
  }

  return {
    /**
     * A noise the simulation emitted, at the radius the simulation gave it.
     *
     * Flattened to plain numbers before it arrives — a cause, a position, a
     * radius — so there is no event object here to reach back through. The
     * radius is used exactly as handed over: this is the one place where the
     * player gets told, by ear, how far what they just did travelled.
     */
    heard({ cause, kind = null, actor = null, x, z, y = 0, radius = 0 }) {
      if (!radius || !Number.isFinite(radius)) return null;
      const name = NAME_FOR[cause] ?? (voiceFor(cause) ? cause : 'impact');
      const at = { x, y, z };

      if (name === 'cart.roll') {
        // Not a thud. The reach of the sound that is already running.
        ridden = { radius, at, until: frames + 30 };
        const decision = decide({ name, cause, at, radius });
        counts.set(name, (counts.get(name) ?? 0) + 1);
        return decision;
      }

      const decision = decide({ name, cause, at, radius });
      /*
       * A heavier thing is a lower thing. The simulation already scales the
       * radius by how hard it landed, so a bigger noise plays slower and deeper
       * without anything here needing to know what was thrown.
       */
      const rate = cause === 'impact' || cause === 'break'
        ? Math.max(0.7, Math.min(1.3, 1.25 - radius / 30))
        : 1;
      fire(decision, { rate });
      return decision;
    },

    /**
     * A sound the game makes that the simulation has no opinion about.
     *
     * Doors, the time clock, the sheet, picking something up and putting it
     * down. They carry their own reach from the catalogue, they are heard by
     * the player and they are heard by nobody else — see the note at the top of
     * this file about why that is not an oversight.
     */
    cue(name, { x, z, y = 0, radius = null, trim = 1, rate = 1 } = {}) {
      const at = { x: x ?? listener.x, y, z: z ?? listener.z };
      const reach = radius ?? REACH[name] ?? 8;
      const decision = decide({ name, at, radius: reach, trim });
      fire(decision, { rate });
      return decision;
    },

    /**
     * One frame of the building.
     *
     * Everything continuous is decided from here: what room you are in, what is
     * under your feet, whether a cart is rolling and who else is walking about.
     * Handed plain numbers once a frame by the reading in `main.js`.
     */
    frame(reading = {}, dt = 1 / 60) {
      frames += 1;
      spent = 0;
      listener = {
        x: reading.listener?.x ?? 0,
        y: reading.listener?.y ?? 0,
        z: reading.listener?.z ?? 0,
        yaw: reading.listener?.yaw ?? 0,
      };
      acuity = Math.max(1, reading.acuity ?? 1);
      engine?.orient?.();

      const recheck = frames % OCCLUSION_EVERY === 0;

      // --- the room ---------------------------------------------------------
      const wanted = reading.zone && AMBIENCE[reading.zone] ? reading.zone : null;
      if (wanted !== zone) {
        const held = running.get('ambience');
        held?.voice?.stop(2);
        running.delete('ambience');
        zone = wanted;
        if (zone) {
          const recipe = AMBIENCE[zone];
          counts.set(`ambience.${zone}`, (counts.get(`ambience.${zone}`) ?? 0) + 1);
          const voice = enabled
            ? engine?.synth?.play(recipe, { gain: 1, hz: 18000, at: null }) ?? null
            : null;
          running.set('ambience', { name: `ambience.${zone}`, voice, decision: null, since: frames });
        }
      }

      /*
       * No footsteps, for the player or for anybody else (D's call). Everybody
       * still moves the rest of the building's sound: the rooms, the cart,
       * whatever they break. `walkers` now only counts the cart's castors.
       */
      if (recheck) {
        for (const key of walkers.keys()) if (key !== 'cart') walkers.delete(key);
      }

      // --- the cart ---------------------------------------------------------
      const cart = reading.cart;
      const rolling = Boolean(cart && cart.speed > 0.35);
      /*
       * A ridden cart carries as far as the simulation last said it does; a
       * pushed one has no noise event of its own, so it carries the ordinary
       * distance a cart being wheeled down a corridor carries. Which is the
       * whole lesson: getting on it makes you louder, and you can hear it.
       */
      const fresh = ridden && frames <= ridden.until;
      const reach = fresh ? ridden.radius : REACH['cart.roll'];
      if (!fresh) ridden = null;
      sustain('cart.roll', 'cart.roll', {
        at: cart ? { x: cart.x, y: 0, z: cart.z } : listener,
        radius: reach,
        on: rolling,
        /* Louder the faster and the heavier, which is what the radius says. */
        trim: rolling ? Math.min(1, 0.35 + (cart.speed ?? 0) / 8) : 0,
        rate: rolling ? Math.max(0.8, Math.min(1.7, 0.7 + (cart.speed ?? 0) / 7)) : 1,
        recheck,
      });
      if (rolling) castors(cart, dt);
      else walkers.delete('cart');
    },

    /** How far through a stride each body is. For a check, not for the game. */
    get walkers() {
      return walkers;
    },

    /** Everything decided lately, newest last. Read-only. */
    probe: () => probe.map((entry) => ({ ...entry })),
    /** How many decisions have ever been made. Only ever goes up. */
    sequence: () => seq,
    /** How many of each sound have been made since the page loaded. */
    tally: () => Object.fromEntries(counts),
    /** What is currently running, by key. */
    playing: () => [...running.keys()],
    /** And what each of them currently sounds like. Read-only. */
    sustained: () =>
      Object.fromEntries([...running].map(([key, held]) => [key, held.decision && { ...held.decision }])),
    get zone() {
      return zone;
    },

    /** Silence, without forgetting anything. For a pause or a panel. */
    setEnabled(next) {
      enabled = Boolean(next);
      if (!enabled) {
        for (const [key, held] of running) {
          held.voice?.stop(0.2);
          running.delete(key);
        }
        zone = null;
      }
    },

    clear() {
      for (const [key, held] of running) {
        held.voice?.stop(0.05);
        running.delete(key);
      }
      walkers.clear();
      probe = [];
      zone = null;
      ridden = null;
    },
  };

  // --- the two things that need the closure --------------------------------

  /**
   * The castors, one tick per turn of a wheel.
   *
   * Separate from the roll and deliberately so. The roll is low and goes
   * through a wall; the castors are high and do not. From the next room you can
   * tell a cart is moving; only in the corridor with it can you tell how fast,
   * which is exactly the information the corridor is for.
   */
  function castors(cart, dt) {
    const held = walkers.get('cart') ?? { phase: 0 };
    held.phase += (cart.speed ?? 0) * dt * 2.6;
    if (held.phase >= 1) {
      held.phase -= Math.floor(held.phase);
      const decision = decide({
        name: 'cart.castor',
        cause: 'castor',
        at: { x: cart.x, y: 0, z: cart.z },
        radius: REACH['cart.castor'],
      });
      fire(decision, { rate: 0.9 + Math.min(0.5, (cart.speed ?? 0) / 14) });
    }
    walkers.set('cart', held);
  }
}

/**
 * What each of the simulation's causes is called in the catalogue.
 *
 * The simulation's words are about what happened; the catalogue's are about
 * what it sounds like, and they are not always the same word. Anything not
 * listed that has a voice of its own uses it, and anything else is a thud —
 * which is the right answer for a noise nobody has written a sound for yet.
 */
const NAME_FOR = Object.freeze({
  impact: 'impact',
  break: 'break',
  ride: 'cart.roll',
  tip: 'cart.tip',
  rustle: 'rustle',
  climb: 'climb',
  charge: 'charge',
  throw: 'throw',
  drop: 'setdown',
});

export { NAME_FOR };
